import React from 'react';
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  Paper,
  Card,
  Button,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  Badge,
  Switch,
  FormControlLabel,
  InputAdornment,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Compare as CompareIcon,
  Assessment as AssessmentIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Group as GroupIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { styled } from '@mui/system';
import { useTranslation } from 'react-i18next';

// Componentes estilizados
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
}));

const ComparisonModeCard = styled(Card)(({ theme, selected }) => ({
  padding: theme.spacing(2),
  border: selected ? `2px solid ${theme.palette.primary.main}` : '1px solid #e0e0e0',
  backgroundColor: selected ? theme.palette.primary.light + '20' : 'white',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: selected ? theme.palette.primary.light + '30' : '#f5f5f5',
  },
}));

export default function ComparisonSetup({
  // Data props
  positions,
  targetPosition,
  groupedApplicants,
  selectedReports,
  comparisonMode,
  idealApplicant,
  loading,
  applicantsCount,
  selectedReportsCount,
  comparisonError,
  
  // Filter props
  applicantFilter,
  showOnlyTargetPosition,
  totalApplicantsCount,
  
  // Callback props
  onTargetPositionChange,
  onReportSelection,
  onComparisonModeChange,
  onIdealApplicantSelection,
  onStartComparison,
  onApplicantFilterChange,
  onShowOnlyTargetPositionChange,
  canStartComparison,
  formatDate,
}) {
  const { t } = useTranslation();

  return (
    <Grid container spacing={3}>
      {/* Panel Izquierdo - Configuración */}
      <Grid item xs={12} md={4}>
        <StyledPaper sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            🎯 {t('comparisonDashboard.targetPosition')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('comparisonDashboard.selectPosition')}
          </Typography>

          <Autocomplete
            value={targetPosition}
            onChange={(event, newValue) => {
              onTargetPositionChange(newValue);
            }}
            options={positions}
            getOptionLabel={(option) => option.name || ''}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box sx={{ width: '100%' }}>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {option.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {option.industria}
                  </Typography>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('positions.searchPlaceholder')}
                placeholder={t('positions.searchPlaceholder')}
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading.positions && <CircularProgress size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            loading={loading.positions}
            noOptionsText={t('comparisonDashboard.noPositionsFound')}
          />

          {targetPosition && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                {t('comparisonDashboard.targetPosition')}:
              </Typography>
              <Typography variant="body2">
                <strong>{targetPosition.name}</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {targetPosition.industria}
              </Typography>
              {targetPosition.descripcion && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  {targetPosition.descripcion.substring(0, 100)}...
                </Typography>
              )}
            </Box>
          )}
        </StyledPaper>

        {targetPosition && applicantsCount > 0 && (
          <StyledPaper sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              🔄 {t('comparisonDashboard.comparisonMode')}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <ComparisonModeCard
                  selected={comparisonMode === '1vsAll'}
                  onClick={() => onComparisonModeChange('1vsAll')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {t('comparisonDashboard.1vsAll')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('comparisonDashboard.1vsAllDescription')}
                      </Typography>
                    </Box>
                  </Box>
                </ComparisonModeCard>
              </Grid>

              <Grid item xs={12}>
                <ComparisonModeCard
                  selected={comparisonMode === 'allVsAll'}
                  onClick={() => onComparisonModeChange('allVsAll')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <GroupIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {t('comparisonDashboard.allVsAll')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('comparisonDashboard.allVsAllDescription')}
                      </Typography>
                    </Box>
                  </Box>
                </ComparisonModeCard>
              </Grid>
            </Grid>
          </StyledPaper>
        )}
      </Grid>

      {/* Panel Central - Lista de Applicants */}
      <Grid item xs={12} md={5}>
        <StyledPaper>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">
              👥 {t('comparisonDashboard.applicants')} ({applicantsCount})
            </Typography>
            {loading.reports && <CircularProgress size={20} />}
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('comparisonDashboard.applicantsWithReports')}
          </Typography>

          {/* Controles de Filtrado */}
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={t('comparisonDashboard.searchApplicantPlaceholder')}
                  value={applicantFilter}
                  onChange={(e) => onApplicantFilterChange(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 1 }}
                />
              </Grid>
              
              {targetPosition && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={showOnlyTargetPosition}
                        onChange={(e) => onShowOnlyTargetPositionChange(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FilterListIcon sx={{ fontSize: 16 }} />
                        <Typography variant="caption">
                          {t('comparisonDashboard.showOnlyTargetPosition')} ({targetPosition.name})
                        </Typography>
                      </Box>
                    }
                  />
                </Grid>
              )}
            </Grid>

            {/* Información de filtrado */}
            {(applicantFilter || showOnlyTargetPosition) && (
              <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 1, border: '1px solid #e9ecef' }}>
                <Typography variant="caption" color="text.secondary">
                  {t('comparisonDashboard.showingResults')}: {applicantsCount} {t('comparisonDashboard.of')} {totalApplicantsCount} {t('comparisonDashboard.applicants').toLowerCase()}
                  {applicantFilter && (
                    <span> • {t('comparisonDashboard.filteredBy')}: "{applicantFilter}"</span>
                  )}
                  {showOnlyTargetPosition && (
                    <span> • {t('comparisonDashboard.onlyTargetPosition')}</span>
                  )}
                </Typography>
              </Box>
            )}
          </Box>

          {loading.reports ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ mt: 2 }}>
                {t('comparisonDashboard.applicantsLoading')}
              </Typography>
            </Box>
          ) : applicantsCount === 0 ? (
            <Alert severity="info">
              {(applicantFilter || showOnlyTargetPosition) ? 
                t('comparisonDashboard.noApplicantsWithFilters') : 
                t('comparisonDashboard.noApplicantsWithReports')
              }
            </Alert>
          ) : (
            <Box sx={{ maxHeight: '600px', overflow: 'auto' }}>
              {Object.entries(groupedApplicants).map(([applicantKey, applicantData]) => (
                <Accordion key={applicantKey} defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {applicantData.applicantName}
                        </Typography>
                        {applicantData.email && (
                          <Typography variant="caption" color="text.secondary">
                            {applicantData.email}
                          </Typography>
                        )}
                        <Box sx={{ mt: 0.5 }}>
                          {[...new Set(applicantData.reports.map(r => r.originalPosition))].map((position, idx) => (
                            <Chip
                              key={idx}
                              label={position}
                              size="small"
                              variant="outlined"
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          ))}
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Badge badgeContent={applicantData.reports.length} color="primary">
                          <AssessmentIcon />
                        </Badge>

                        {selectedReports[applicantKey] && (
                          <CheckCircleIcon color="success" />
                        )}

                        {comparisonMode === '1vsAll' && selectedReports[applicantKey] && (
                          <Checkbox
                            checked={idealApplicant === applicantKey}
                            onChange={() => onIdealApplicantSelection(applicantKey)}
                            icon={<RadioButtonUncheckedIcon />}
                            checkedIcon={<CheckCircleIcon />}
                            color="secondary"
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                      </Box>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                     {t('comparisonDashboard.selectReportForComparison')}:
                    </Typography>

                    <List dense>
                      {applicantData.reports.map((report) => (
                        <ListItem
                          key={report.id}
                          button
                          selected={selectedReports[applicantKey] === report.id}
                          onClick={() => onReportSelection(applicantKey, report.id)}
                          sx={{
                            borderRadius: 1,
                            mb: 1,
                            border: selectedReports[applicantKey] === report.id ?
                              '2px solid' : '1px solid',
                            borderColor: selectedReports[applicantKey] === report.id ?
                              'primary.main' : 'grey.300'
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2">
                                  {t('report')} {report.id.substring(0, 8)}
                                </Typography>
                                <Chip
                                  label={report.originalPosition}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              </Box>
                            }
                            secondary={`${t('created')}: ${formatDate(report.created_at)}`}
                          />
                          {selectedReports[applicantKey] === report.id && (
                            <ListItemSecondaryAction>
                              <CheckCircleIcon color="primary" />
                            </ListItemSecondaryAction>
                          )}
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </StyledPaper>
      </Grid>

      {/* Panel Derecho - Resumen y Acciones */}
      <Grid item xs={12} md={3}>
        <StyledPaper>
          <Typography variant="h6" gutterBottom>
            📋 {t('summary')}
          </Typography>

          {targetPosition ? (
            <>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('comparisonDashboard.targetPosition')}:
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {targetPosition.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {targetPosition.industria}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />
            </>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('comparisonDashboard.selectPositionToContinue')}
            </Alert>
          )}

          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('comparisonDashboard.availableApplicants')}:
            </Typography>
            <Typography variant="h4" color="primary">
              {applicantsCount}
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('comparisonDashboard.selectedReports')}:
            </Typography>
            <Typography variant="h4" color="primary">
              {selectedReportsCount}
            </Typography>
          </Box>

          {comparisonMode && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('comparisonDashboard.comparisonMode')}:
              </Typography>
              <Chip
                label={comparisonMode === '1vsAll' ? t('comparisonDashboard.1vsAll') : t('comparisonDashboard.allVsAll')}
                color="primary"
                icon={comparisonMode === '1vsAll' ? <PersonIcon /> : <GroupIcon />}
              />
            </Box>
          )}

          {comparisonMode === '1vsAll' && idealApplicant && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('comparisonDashboard.idealApplicant')}:
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {idealApplicant}
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          <Button
            variant="contained"
            size="large"
            fullWidth
            disabled={!canStartComparison() || loading.comparison}
            startIcon={loading.comparison ? <CircularProgress size={20} /> : <CompareIcon />}
            onClick={onStartComparison}
            sx={{ mb: 2 }}
          >
            {loading.comparison ? t('comparisonDashboard.starting') : t('comparisonDashboard.start')}
          </Button>

          {!canStartComparison() && (
            <Alert severity="warning" size="small">
              <Typography variant="caption">
                {t('comparisonDashboard.requirements.title')}:
                <br />
                • {t('comparisonDashboard.requirements.reqOne')}
                <br />
                • {t('comparisonDashboard.requirements.reqTwo')}
                <br />
                • {t('comparisonDashboard.requirements.reqThree')}
                {comparisonMode === '1vsAll' && <><br />• {t('comparisonDashboard.requirements.reqFour')}</>}
              </Typography>
            </Alert>
          )}

          {comparisonError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <Typography variant="caption">
                {comparisonError}
              </Typography>
            </Alert>
          )}
        </StyledPaper>
      </Grid>
    </Grid>
  );
}